import type { Locale } from '../i18n/locales'

/**
 * Full Gregorian month names, hand-written rather than read through `Intl.DateTimeFormat` —
 * ICU's `he` long-month form prefixes a `ב` ("ב-אוגוסט") and that prefix's presence is not
 * consistent across ICU builds (`i18n/format.ts`'s own `stripIcuDisambiguation` comment
 * records the same class of drift). The sidebar's version stamp needs one exact literal
 * (`ui-scale-refresh-handoff.md` A6), not whatever the runtime's ICU data happens to emit.
 */
const MONTH_NAMES: Readonly<Record<Locale, readonly string[]>> = {
  he: [
    'ינואר',
    'פברואר',
    'מרץ',
    'אפריל',
    'מאי',
    'יוני',
    'יולי',
    'אוגוסט',
    'ספטמבר',
    'אוקטובר',
    'נובמבר',
    'דצמבר',
  ],
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  // Genitive, not nominative: `Drawer` renders these as `{day} {month} {year}`, and Russian
  // spells that date "17 августа 2026" — never "17 август 2026". The nominative forms belong to
  // a month named on its own, which this surface never does.
  ru: [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря',
  ],
  // Lower case, and not a style choice: Italian capitalises a month name only at the start of a
  // sentence, and `Drawer` renders these mid-phrase as `{day} {month} {year}` — "17 agosto 2026".
  it: [
    'gennaio',
    'febbraio',
    'marzo',
    'aprile',
    'maggio',
    'giugno',
    'luglio',
    'agosto',
    'settembre',
    'ottobre',
    'novembre',
    'dicembre',
  ],
  // Lower case for the same reason the Italian row is: Ukrainian capitalises a month name only at
  // the start of a sentence, and `Drawer` renders these mid-phrase as `{day} {month} {year}`.
  // These are the **genitive** forms — `серпня`, not the nominative `серпень` — because that is
  // the case a Ukrainian date puts the month in, and the nominative would read as a heading
  // rather than as a date.
  uk: [
    'січня',
    'лютого',
    'березня',
    'квітня',
    'травня',
    'червня',
    'липня',
    'серпня',
    'вересня',
    'жовтня',
    'листопада',
    'грудня',
  ],
}

export interface BuildDateParts {
  /** No leading zero — `formatBuildDate.test.ts` pins this against the 1st of a month. */
  readonly day: string
  readonly month: string
  readonly year: string
}

/**
 * `17 אוגוסט 2026` / `17 August 2026` (A6). Read in UTC, matching `BUILD.builtAt`'s own
 * instant — the build date should not shift a day depending on the viewer's time zone.
 */
export function formatBuildDate(iso: string, locale: Locale): BuildDateParts {
  const date = new Date(iso)
  // `getUTCMonth()` is always 0-11, so the lookup is total — `noUncheckedIndexedAccess` just
  // cannot see that from the array's type.
  const month = MONTH_NAMES[locale][date.getUTCMonth()]!
  return {
    day: String(date.getUTCDate()),
    month,
    year: String(date.getUTCFullYear()),
  }
}
